/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/


// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";


import { useMemo, useRef, useState } from "react";
import moment from "moment";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from '@mui/material/Box';
import Divider from "@mui/material/Divider";
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Switch from '@mui/material/Switch';
import Select from '@mui/material/Select';
import SoftTypography from "components/SoftComponents/SoftTypography";

import PlaceholderCard from "template/examples/Cards/PlaceholderCard";
import Map from "components/Maps/Map";


// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";

// Soft UI Dashboard PRO React example components

import Calendar from "components/Calendars/ClearwayCalendar/Calendar";
import { processDevicesData } from "utils/device";
import { processGroupData } from "utils/group";
import { Collapse, List, ListItem, ListItemButton, ListItemIcon, ListItemText, ListSubheader } from "@mui/material";
import { ExpandLess, ExpandMore, StarBorder } from "@mui/icons-material";
import SoftInput from "components/SoftComponents/SoftInput";



const mapbox_settings = {
    style: 'mapbox://styles/harrymontano/cm01omrnz000u01omf2uegk93?optimize=true',
    zoom: 14,
}

function ClearwayGroupSelector({ event, groups, devices, selectedGroups, setSelectedGroups, openedTabs, setOpenedTabs, isView, showAdvancedDeviceLevel }) {
    var groups_dict = {};

    groups = groups.filter((row) => row.type == "clearways");

    for (let group of groups) {
        groups_dict[group.group_id] = group;
    }

    const handleOpenTab = (e, group) => {
        if (!e.defaultPrevented) {
            let new_array = openedTabs.slice();
            var index = new_array.indexOf(group.group_id);

            // console.log(new_array);
            // console.log(index);

            if (index >= 0) { //is open
                new_array.splice(index, 1);
            }
            else {
                new_array.push(group.group_id);
            }

            setOpenedTabs(new_array);
        }
    };

    const exceptions = [
        {
            label: "-",
            value: ""
        },
        {
            label: "Buses Excepted",
            value: "buses_excepted"
        }
    ];

    function handleClick(e, group, child_indexes, index) {
        e.preventDefault();
        let new_array = selectedGroups.slice();
        var parent_index = new_array.map(row => row.group_id).indexOf(group.group_id);

        if (parent_index >= 0) { //is selected
            new_array.splice(parent_index, 1);
        }
        else {
            new_array.push({
                group_id: group.group_id,
                group_parent_id: group.parent_id,
                type: group.type,
                subgroups: group.subgroups,
                exception: null,
                start_time_override: null,
                end_time_override: null,
            });
        }

        if (typeof group.parent_id == 'undefined') { //is a parent group, change all is_selected for child groups
            if (child_indexes.length > 0) {
                for (var child of child_indexes) {
                    var child_index = new_array.map(row => row.group_id).indexOf(child);

                    if (parent_index >= 0) { //is selected
                        new_array.splice(child_index, 1);
                    }
                    else {
                        if (child_index < 0) {
                            new_array.push({
                                group_id: child,
                                parent_id: 'undefined',
                                type: group.type,
                                subgroups: group.subgroups,
                                exception: null,
                                start_time_override: null,
                                end_time_override: null,
                            });
                        }
                    }
                }
            }
        }

        new_array = new_array.map(function ({ group_id, parent_id, type, subgroups, exception, start_time_override, end_time_override }) {
            if (start_time_override == "") {
                start_time_override = null;
            }
            if (end_time_override == "") {
                end_time_override = null;
            }

            return { group_id, parent_id, type, subgroups, exception, start_time_override, end_time_override }
        });

        setSelectedGroups(new_array);
    }

    function setException(new_value, index, is_parent, child_indexes) {
        let groups = selectedGroups.slice();
        groups[index].exception = new_value;
        // console.log(groups);

        if (is_parent) { //is a parent group, change all is_selected for child groups
            for (var group of groups) {
                child_indexes.forEach(function (child) {
                    if (group.group_id == child) {
                        group.exception = new_value;
                    }
                });
            }
        }

        setSelectedGroups(groups);
    }

    function applyValues(field, group, child_groups, child_indexes) {
        let groups = selectedGroups.slice();
        var value = "";

        for (var selected_group of groups) {
            var child_group = child_groups.filter(row => row.group_id == selected_group.group_id);

            if ((child_group.length > 0 && child_group[0].parent_id == group.group_id) || selected_group.group_id == group.group_id) {
                value = field == 'start_time' ? selected_group.start_time_override : selected_group.end_time_override;
            }
        }

        return {
            "value": value,
            "onChange": (new_value) => {
                setTimeOverrides(field, group, child_groups, child_indexes, new_value.target.value);
            }
        }
    }

    function setTimeOverrides(field, group, children, child_indexes, new_value) {
        let groups = selectedGroups.slice();
        // console.log(groups)
        if (!new_value.length) {
            new_value = null;
        }

        for (var selected_group of groups) {
            var child_group = children.filter(row => row.group_id == selected_group.group_id);

            if ((child_group.length > 0 && child_group[0].parent_id == group.group_id) || selected_group.group_id == group.group_id) {
                if (field == 'start_time') {
                    selected_group.start_time_override = new_value == "" ? null : new_value;
                }
                else {
                    selected_group.end_time_override = new_value == "" ? null : new_value;
                }

                if (typeof group.parent_id == 'undefined') { //is a parent group, change all start/end times for child groups
                    child_indexes.forEach(function (child) {
                        var child_group = groups.filter(row => row.group_id == child);

                        if (child_group.length > 0) {
                            if (field == 'start_time') {
                                child_group[0].start_time_override = new_value == "" ? null : new_value;
                            }
                            else {
                                child_group[0].end_time_override = new_value == "" ? null : new_value;
                            }
                        }
                    });
                }
            }
        }

        console.log(groups);

        setSelectedGroups(groups);
    }

    var selectedGroupIndexes = selectedGroups.map(row => row.group_id);
    var openedGroupTabIndexes = openedTabs.map(row => row.group_id);

    var groups_data = groups.filter((row) => row.type == "clearways").map(function (group) {
        var ret = processGroupData(group, devices);
        return ret;
    });

    function generateGroupCards(groups_data, group_id, selectedGroupIndexes, selectedGroups) {

        var groups = [];

        var parent_group = groups_data.filter(row => row.group_id == group_id);
        parent_group = parent_group[0];
        groups.push(parent_group);
        var child_groups = groups_data.filter(row => row.parent_id == group_id);
        groups.push(...child_groups);

        var child_indexes = child_groups.map(row => { return row.group_id });

        var cards = groups.sort((a, b) => {
            if (a.name < b.name) {
                return -1;
            }
            if (a.name > b.name) {
                return 1;
            }
            return 0;
        }).map(function (group, index) {

            const group_index = selectedGroupIndexes.indexOf(group.group_id);
            const is_selected = group_index >= 0;
            const exception = is_selected ? selectedGroups[group_index].exception : "";

            const group_type = typeof group.parent_id == 'undefined' ? 'Main Group' : 'Subgroup';

            if (!group.all_updated) {
                var polygon_colour = "#ff667c";
            }
            else if (group.devices_monitored > 0 && showAdvancedDeviceLevel) {
                var polygon_colour = "#edaa00";
            }
            else {
                var polygon_colour = "#58ba2d";
            }

            return (
                <Grid item xxs={12} sm={6} lg={6} xl={4} key={group.group_id} px={2} sx={{ paddingTop: '0px !important', paddingLeft: '12px !important' }}>
                    <br />
                    <Card sx={{ backgroundColor: (is_selected ? "#f5f5f5" : ""), border: "rgb(210, 214, 218) 1px solid" }} >
                        <SoftBox m={2} mt={1.5}>
                            <SoftBox display="flex" >
                                <SoftTypography variant="h6" pt={0.5} >{group.name}</SoftTypography>
                                <SoftTypography
                                    sx={{
                                        ml: "auto",
                                        alignSelf: "flex-start",
                                        // py: 1.25,
                                    }}
                                >
                                    {isView && (
                                        <Switch checked={is_selected} onClick={(e) => { handleClick(e, group, child_indexes, index) }} sx={{ justifyContent: "space-between" }} disabled />
                                    )}
                                    {!isView && (
                                        <Switch checked={is_selected} onClick={(e) => { handleClick(e, group, child_indexes, index) }} sx={{ justifyContent: "space-between" }} />
                                    )}
                                </SoftTypography>
                            </SoftBox>
                            <SoftBox mb={1}>
                                {group.devices.length == 1 && (
                                    <SoftTypography variant="caption">
                                        {`${group_type}, ${group.devices.length} Device`}
                                    </SoftTypography>
                                )}
                                {group.devices.length != 1 && (
                                    <SoftTypography variant="caption">
                                        {`${group_type}, ${group.devices.length} Devices`}
                                    </SoftTypography>
                                )}
                            </SoftBox>
                            <SoftBox mb={2}>
                                {is_selected ? (
                                    <>
                                        {isView ? (
                                            <Select
                                                value={exception}
                                                onChange={(e) => { setException(e.target.value, group_index, typeof group.parent_id == 'undefined', child_indexes); }}
                                                disabled
                                            >
                                                {exceptions.map(function (row) {
                                                    return <MenuItem key={row.value} value={row.value}>{row.label}</MenuItem>
                                                })}
                                            </Select>
                                        ) : (
                                            <Select
                                                value={exception}
                                                onChange={(e) => { setException(e.target.value, group_index, typeof group.parent_id == 'undefined', child_indexes); }}
                                            >
                                                {exceptions.map(function (row) {
                                                    return <MenuItem key={row.value} value={row.value}>{row.label}</MenuItem>
                                                })}
                                            </Select>
                                        )}
                                    </>
                                ) : (
                                    <Select
                                        value={exception}
                                        onChange={(e) => { setException(e.target.value, group_index, typeof group.parent_id == 'undefined', child_indexes); }}
                                        disabled
                                    >
                                        {exceptions.map(function (row) {
                                            return <MenuItem key={row.value} value={row.value}>{row.label}</MenuItem>
                                        })}
                                    </Select>
                                )}
                            </SoftBox>
                            <Card sx={{ height: '200px', width: '100%' }} onClick={(e) => { e.stopPropagation() }}>
                                <Map selected_group={group} parent_group={parent_group.group_id == group.group_id ? {} : parent_group} colour={polygon_colour} settings={mapbox_settings} padding={30} ></Map>
                            </Card>
                        </SoftBox>
                    </Card>
                    <br />
                </Grid >
            )
        });

        return (
            <SoftBox mt={3} pl={3}>
                <List component="div" disablePadding>
                    <Grid container spacing={3}>
                        {cards}
                    </Grid>
                </List>
            </SoftBox>
        );
    }


    // set up openedGroupTabs similar to selectedGroups, indexing could work almost the same
    // need to then display each group and subgroups under the list item, with the group as first position (before subgroups)
    // then set up toggle all when checking the switch for the group (turn on, turns on all subgroups, vice versa.

    var parent_groups = groups_data.filter(row => typeof row.parent_id == 'undefined').sort((a, b) => {
        if (a.name < b.name) {
            return -1;
        }
        if (a.name > b.name) {
            return 1;
        }
        return 0;
    }).map(function (group, index) {

        // console.log(index);
        // console.log(group.group_id, group.name);

        const group_tab_index = openedGroupTabIndexes.indexOf(group.group_id);
        const is_opened = group_tab_index >= 0;

        const group_index = selectedGroupIndexes.indexOf(group.group_id);
        const is_selected = group_index >= 0;

        var has_selected = true;

        var groups = [];
        var child_indexes = [];

        var child_groups = groups_data.filter(row => row.parent_id == group.group_id);

        if (child_groups.length > 0) {
            child_indexes = child_groups.map(row => { return row.group_id });
        }

        const group_any_selected = selectedGroupIndexes.filter((row) => child_indexes.indexOf(row) > -1).length > 0 || group_index >= 0;

        var parent_child_groups = [];
        parent_child_groups.push(group);
        parent_child_groups.push(...child_groups);

        groups.push(group);
        groups.push(...child_groups);

        var clearway_timing = "";

        if (isView) {
            has_selected = false;
            groups.forEach(function (group) {
                if (group_any_selected) {
                    has_selected = true;
                    var group_override_times = event.groups.filter((row) => child_indexes.indexOf(row.group_id) > -1 || group.group_id == row.group_id)[0];

                    // console.log(group_override_times)

                    if (group_override_times.start_time_override != null) {
                        var time_arr = group_override_times.start_time_override.split(':');
                        if (Number(time_arr[1]) > 0) {
                            var time_format = 'h:mmA';
                        }
                        else {
                            var time_format = 'hA';
                        }

                        if (event.is_multi_day) {
                            clearway_timing = moment(event.start_time).tz("Australia/Sydney").startOf('day').add(time_arr[0], 'hours').add(time_arr[1], 'minutes').format(`${time_format}, ddd Do MMM`);
                        }
                        else {
                            clearway_timing = moment(event.start_time).tz("Australia/Sydney").startOf('day').add(time_arr[0], 'hours').add(time_arr[1], 'minutes').format(`${time_format}`);
                        }
                    }
                    else {
                        if (Number(moment(event.start_time).format('mm')) > 0) {
                            var time_format = 'h:mmA';
                        }
                        else {
                            var time_format = 'hA';
                        }
                        clearway_timing = event.is_multi_day ? moment(event.start_time).format(`${time_format}, ddd Do MMM`) : moment(event.start_time).format(`${time_format}`);
                    }

                    if (group_override_times.end_time_override != null) {
                        var time_arr = group_override_times.end_time_override.split(':');
                        if (Number(time_arr[1]) > 0) {
                            var time_format = 'h:mmA';
                        }
                        else {
                            var time_format = 'hA';
                        }

                        if (event.is_multi_day) {
                            clearway_timing += ` - ${moment(event.end_time).tz("Australia/Sydney").startOf('day').add(time_arr[0], 'hours').add(time_arr[1], 'minutes').format(`${time_format}, ddd Do MMM`)}`;
                        }
                        else {
                            clearway_timing += ` - ${moment(event.end_time).tz("Australia/Sydney").startOf('day').add(time_arr[0], 'hours').add(time_arr[1], 'minutes').format(`${time_format}`)}, ${moment(event.start_time).tz("Australia/Sydney").format('ddd Do MMM')}`;
                        }
                    }
                    else {
                        if (Number(moment(event.end_time).format('mm')) > 0) {
                            var time_format = 'h:mmA';
                        }
                        else {
                            var time_format = 'hA';
                        }

                        if (event.is_multi_day) {
                            clearway_timing += ` - ${moment(event.end_time).format(`${time_format}, ddd Do MMM`)}`;
                        }
                        else {
                            clearway_timing += ` - ${moment(event.end_time).format(`${time_format}`)}, ${moment(event.start_time).tz("Australia/Sydney").format('ddd Do MMM')}`;
                        }
                    }
                }
            })
        }

        return (
            <div key={group.group_id}>
                {has_selected && (
                    <ListItem>
                        <ListItemButton key={group.group_id} onClick={(e) => handleOpenTab(e, group)}>
                            <ListItemText>
                                <Grid container>
                                    <Grid item xxs={6}>
                                        <SoftTypography variant="h6" pt={0.5}>
                                            {group.name}
                                        </SoftTypography>
                                    </Grid>
                                    {isView && (
                                        <Grid item xxs={6}>
                                            <SoftTypography variant="h6" pt={0.5}>
                                                {clearway_timing}
                                            </SoftTypography>
                                        </Grid>
                                    )}
                                </Grid>
                            </ListItemText>
                            {isView && (
                                <SoftBox display={'flex'} flexDirection={'row'} justifyContent={'center'} alignItems={'center'}>
                                    <SoftInput type="time" step="900" input={{ placeholder: "Select a date" }} onClick={(e) => { e.preventDefault() }} {...applyValues('start_time', group, child_groups, child_indexes)} sx={{ marginRight: '5px', marginLeft: '5px' }} disabled />
                                    <SoftInput type="time" step="900" input={{ placeholder: "Select a date" }} onClick={(e) => { e.preventDefault() }} {...applyValues('end_time', group, child_groups, child_indexes)} sx={{ marginRight: '5px', marginLeft: '5px' }} disabled />
                                    <Switch checked={is_selected} onClick={(e) => { handleClick(e, group, child_groups, child_indexes, index) }} sx={{ marginRight: '15px', marginLeft: '5px' }} disabled />
                                </SoftBox>
                            )}
                            {!isView && (
                                <SoftBox display={'flex'} flexDirection={'row'} justifyContent={'center'} alignItems={'center'}>
                                    {group_any_selected && (
                                        <SoftBox display={'flex'} flexDirection={'row'} justifyContent={'center'} alignItems={'center'}>
                                            <SoftInput type="time" step="900" input={{ placeholder: "Select a date" }} onClick={(e) => { e.preventDefault() }} {...applyValues('start_time', group, child_groups, child_indexes)} sx={{ marginRight: '5px', marginLeft: '5px' }} />
                                            <SoftInput type="time" step="900" input={{ placeholder: "Select a date" }} onClick={(e) => { e.preventDefault() }} {...applyValues('end_time', group, child_groups, child_indexes)} sx={{ marginRight: '5px', marginLeft: '5px' }} />
                                        </SoftBox>
                                    )}
                                    {!group_any_selected && (
                                        <SoftBox display={'flex'} flexDirection={'row'} justifyContent={'center'} alignItems={'center'}>
                                            <SoftInput type="time" step="900" input={{ placeholder: "Select a date" }} onClick={(e) => { e.preventDefault() }} {...applyValues('start_time', group, child_groups, child_indexes)} sx={{ marginRight: '5px', marginLeft: '5px' }} disabled />
                                            <SoftInput type="time" step="900" input={{ placeholder: "Select a date" }} onClick={(e) => { e.preventDefault() }} {...applyValues('end_time', group, child_groups, child_indexes)} sx={{ marginRight: '5px', marginLeft: '5px' }} disabled />
                                        </SoftBox>
                                    )}
                                    <Switch checked={is_selected} onClick={(e) => { handleClick(e, group, child_indexes, index) }} sx={{ marginRight: '15px', marginLeft: '5px' }} />
                                </SoftBox>
                            )}
                            {is_opened ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                    </ListItem>
                )
                }
                {!has_selected && (
                    <ListItemButton key={group.group_id} onClick={(e) => handleOpenTab(e, group)} disabled>
                        <ListItemText>
                            <SoftBox display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
                                <SoftBox>
                                    <SoftTypography variant="h6" pt={0.5}>
                                        {group.name}
                                    </SoftTypography>
                                </SoftBox>
                                <SoftBox display={'flex'} flexDirection={'row'} justifyContent={'center'} alignItems={'center'}>
                                    <SoftInput type="time" step="900" input={{ placeholder: "Select a date" }} onClick={(e) => { e.preventDefault() }} {...applyValues('start_time', group, child_groups, child_indexes)} sx={{ marginRight: '5px', marginLeft: '5px' }} disabled />
                                    <SoftInput type="time" step="900" input={{ placeholder: "Select a date" }} onClick={(e) => { e.preventDefault() }} {...applyValues('end_time', group, child_groups, child_indexes)} sx={{ marginRight: '5px', marginLeft: '5px' }} disabled />
                                    <Switch checked={is_selected} onClick={(e) => { handleClick(e, group, child_indexes, index) }} sx={{ marginRight: '15px', marginLeft: '5px' }} disabled />
                                </SoftBox>
                            </SoftBox>
                        </ListItemText>
                        {is_opened ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                )}
                <Collapse in={openedTabs.indexOf(group.group_id) > -1} timeout="auto" unmountOnExit>
                    {generateGroupCards(groups_data, group.group_id, selectedGroupIndexes, selectedGroups)}
                </Collapse>
            </div >
        )
    });

    return (
        <SoftBox mb={{ xxs: 1, lg: 3 }} mt={{ xxs: 1, lg: 1 }}>
            <List
                sx={{ width: '100%' }}
                component="nav"
                aria-labelledby="nested-list-subheader"
                subheader={
                    <ListSubheader component="div" id="nested-list-subheader">
                        <SoftBox display={'flex'} flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'}>
                            <SoftBox>
                                <SoftTypography variant='button' fontWeight='bold'>
                                    Group Name
                                </SoftTypography>
                            </SoftBox>
                            {isView && (
                                <SoftBox>
                                    <SoftTypography variant='button' fontWeight='bold'>
                                        SEC Times
                                    </SoftTypography>
                                </SoftBox>
                            )}
                            <SoftBox>
                                <SoftTypography variant='button' fontWeight='bold' sx={{ marginRight: '24px', marginLeft: '15px' }}>
                                    Start Override
                                </SoftTypography>
                                <SoftTypography variant='button' fontWeight='bold' sx={{ marginRight: '8px', marginLeft: '15px' }}>
                                    End Override
                                </SoftTypography>
                                <SoftTypography variant='button' fontWeight='bold' sx={{ marginRight: '15px', marginLeft: '15px' }}>
                                    Select All
                                </SoftTypography>
                            </SoftBox>
                        </SoftBox>
                    </ListSubheader>
                }
            >
                {parent_groups}
            </List>
        </SoftBox>
    );

}

// Typechecking props for the Calendar
ClearwayGroupSelector.propTypes = {
    event: PropTypes.object,
    groups: PropTypes.array,
    devices: PropTypes.array,
    selectedGroups: PropTypes.array,
    setSelectedGroups: PropTypes.func,
    openedTabs: PropTypes.array,
    setOpenedTabs: PropTypes.func,
    isView: PropTypes.bool,
    showAdvancedDeviceLevel: PropTypes.bool
};


export default ClearwayGroupSelector;